import type { TabsProps } from "antd";
import { Layout, Row, Space, Tabs, Typography } from "antd";

import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import SearchIcon from "../../../assets/icons/search-white.svg";
import Popcorn from "../../../assets/images/popcorn.webp";
import ToDeliver from "../../../assets/images/to-deliver.webp";
import FooterComponent from "../../../components/FooterComponent";
import FormationModalComponent from "../../../components/Formations/Creators/Modals";
import MediaPushNotifyBanner from "../../../components/MediaPushNotifyBanner";
import MessageTabLabelComponent from "../../../components/MessageTabLabelComponent";
import MessageTabListComponent from "../../../components/MessageTabListComponent";
import SearchComponent from "../../../components/Search/SearchComponent";
import { CONVERSATIONS_FILTER_TYPES, CONVERSATION_TYPES, C_CURRENT_TAB, FOOTER_IDS } from "../../../helpers/constant";
import { useLocalStorage } from "../../../helpers/hooks/useLocalStorageHook";
import useFetchUserSettings from "../../../hooks/userHooks/useFetchUserSettings";
import { useAppSelector } from "../../../redux/hooks";
import {
  getUserConversations,
  resetCurrentFilter,
  resetSearchConversations,
  resetType,
  searchConversation,
  setSearchValue,
  updateUnreadTypes,
} from "../../../redux/messages/messagesSlice";
import { selectFilteredConversations, selectUnreadTypes } from "../../../redux/messages/Selectors/messageSeletors";
import { AppDispatch, StoreState } from "../../../redux/store";
import { updateTutorialVisibilityStatus } from "../../../redux/users/usersSlice";

import { handleDisplayMediaPushNotifyBanner } from "../../../utls/FunctionsUtil";
import "./index.less";

const CreatorMessagePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { searchValue, isSearching } = useSelector((state: StoreState) => state.messages);
  const unreadTypes = useAppSelector(selectUnreadTypes);

  const dispatch = useDispatch<AppDispatch>();
  const shouldFetch = useRef(true);
  const fetchedActiveKeys = useRef(new Set<string>());
  const [activeKey, setActiveKey] = useState(window.localStorage.getItem(C_CURRENT_TAB) || CONVERSATION_TYPES.GENERAL);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  //const [formationModalIsVisible, setFormationModalIsVisible] = useState<boolean>(false);
  const { currentUserSettings } = useFetchUserSettings();
  const conversationsData = useAppSelector((state) => selectFilteredConversations(state, activeKey, CONVERSATIONS_FILTER_TYPES.ALL));
  const isMediaPushSentTime = useLocalStorage("mediaPushSentTime");

  // useEffect(() => {
  //   // TODO: code to refactor
  //   //checkModalStatus("hasSeenChatModal", setFormationModalIsVisible, currentUserSettings);

  //   if (shouldFetch.current) {
  //     shouldFetch.current = false;
  //     dispatch(getUnreadByTypes());
  //   }

  //   socket.on("unread-messages", handleUnreadMessages);
  //   return () => {
  //     socket.off("unread-messages", handleUnreadMessages);
  //   };
  // }, []);

  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes("conversation") && !window.location.pathname.includes("message")) {
        dispatch(resetCurrentFilter());
      }
    };
  }, [dispatch, isSearching]);

  useEffect(() => {
    if (!fetchedActiveKeys.current.has(activeKey) && activeKey !== CONVERSATION_TYPES.SEARCH && conversationsData.length === 0) {
      fetchedActiveKeys.current.add(activeKey);
      dispatch(getUserConversations({ page: 1, type: activeKey }));
    }
  }, [activeKey, conversationsData, dispatch]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchValue.length >= 3) {
        dispatch(resetSearchConversations());
        dispatch(resetCurrentFilter());

        dispatch(
          searchConversation({
            search: searchValue,
            page: 1,
            type: activeKey,
          })
        );
      }
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [searchValue]);

  function handleUnreadMessages(data: any) {
    if (data && Array.isArray(data)) {
      const unreadTypesObject: { [key: string]: boolean } = {};
      data.forEach((object: { type: string; hasNewMessages: boolean }) => {
        unreadTypesObject[object.type] = object.hasNewMessages;
      });
      dispatch(updateUnreadTypes(unreadTypesObject));
    }
  }

  const onChangeTab = (activeKey: string) => {
    setActiveKey(activeKey);
    dispatch(resetCurrentFilter());
    dispatch(setSearchValue(""));
    window.localStorage.setItem(C_CURRENT_TAB, activeKey);
  };

  const toggleSearchVisibility = () => {
    setIsSearchVisible((prev) => !prev);
  };

  const tabs: TabsProps["items"] = [
    {
      key: CONVERSATION_TYPES.TO_DELIVER,
      label: (
        <MessageTabLabelComponent
          icon={ToDeliver}
          label={t("to-deliver")}
          isNew={unreadTypes?.[CONVERSATION_TYPES.TO_DELIVER]}
          isActive={activeKey === CONVERSATION_TYPES.TO_DELIVER}
        />
      ),
      children: <MessageTabListComponent activeKey={CONVERSATION_TYPES.TO_DELIVER} />,
    },
    {
      key: CONVERSATION_TYPES.GENERAL,
      label: (
        <MessageTabLabelComponent
          icon={Popcorn}
          label={t("general")}
          isNew={unreadTypes?.[CONVERSATION_TYPES.GENERAL]}
          isActive={activeKey === CONVERSATION_TYPES.GENERAL}
        />
      ),
      children: <MessageTabListComponent activeKey={CONVERSATION_TYPES.GENERAL} />,
    },
  ];

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const trimmedSearchTerm = e.currentTarget.value.trim();

    dispatch(setSearchValue(trimmedSearchTerm));
    if (trimmedSearchTerm === "") {
      dispatch(resetType());
    }
  };

  const handleTutorialRead = async () => {
    dispatch(updateTutorialVisibilityStatus("hasSeenChatModal"));
  };

  return (
    <Layout>
      {handleDisplayMediaPushNotifyBanner(isMediaPushSentTime) && <MediaPushNotifyBanner />}
      <Layout.Content className="content creatorMessageContentWrapper gradient-background-black-blue">
        <Row className="justify-content-between header-title-container-95 mb-6">
          <Typography.Text className="header-title-black-35">{t("messaging")}</Typography.Text>
          <Space className="gap-13">
            {/* <img src={EditIcon} width={26} alt="edit" className="cursor-pointer" onClick={() => navigate("/common/messages/new")} /> */}
            <img src={SearchIcon} width={24} alt="edit" className="cursor-pointer" onClick={() => toggleSearchVisibility()} />
          </Space>
        </Row>
        {isSearchVisible && (
          <Row className="mb-20">
            <SearchComponent placeholder={t("search-2")} value={searchValue} handleChange={handleSearch} />
          </Row>
        )}
        {isSearching ? (
          <MessageTabListComponent activeKey={activeKey} type={CONVERSATION_TYPES.SEARCH} />
        ) : (
          <Tabs
            // className="chatTabs"
            className="chatTabs creator-nav-tabs"
            centered
            activeKey={activeKey}
            items={tabs}
            destroyInactiveTabPane={true}
            onChange={onChangeTab}
          />
        )}
        {currentUserSettings && !currentUserSettings.visibilityStatus.hasSeenChatModal && (
          <FormationModalComponent id="chat" visible={true} handleTutorialRead={handleTutorialRead} />
        )}
      </Layout.Content>
      <FooterComponent type="creator" page={FOOTER_IDS.MESSAGING} />
    </Layout>
  );
};

export default CreatorMessagePage;
